import { SelectInput, TextInput } from "@/components/shared/FormInputs";
import { WAITLIST_PRIMARY_PRACTICE_OPTIONS } from "@/types/waitlist";
import type { WaitlistFormFieldErrors, WaitlistSurveyState } from "@/types/waitlist";
import { cn } from "@paribus/shared-ui";

const caseFlowLabelClass = (selected: boolean) =>
  cn(
    "flex items-center justify-center px-4 py-3 rounded-xl border-2 cursor-pointer transition-all duration-200",
    selected
      ? "border-primary bg-primary-subtle text-primary-emphasis shadow-sm"
      : "border-border bg-card text-muted-foreground hover:border-input"
  );

interface WaitlistStepSurveyProps {
  visible: boolean;
  survey: WaitlistSurveyState;
  errors: WaitlistFormFieldErrors;
  onSurveyChange: (patch: Partial<WaitlistSurveyState>) => void;
}

export const WaitlistStepSurvey = ({
  visible,
  survey,
  errors,
  onSurveyChange,
}: WaitlistStepSurveyProps) => {
  const practiceAreaOptions = WAITLIST_PRIMARY_PRACTICE_OPTIONS.map((opt) => ({
    value: opt,
    label: opt,
  }));

  return (
    <div
      className={cn(
        "transition-all duration-300",
        visible ? "block animate-in fade-in slide-in-from-right-4" : "hidden"
      )}
    >
      <SelectInput
        label="What's your primary practice area?"
        value={survey.primaryPracticeArea}
        options={practiceAreaOptions}
        placeholder="Select your primary practice area"
        onChange={(v) => onSurveyChange({ primaryPracticeArea: v })}
        error={errors.primaryPracticeArea}
      />

      <TextInput
        type="number"
        label="How many reports do you plan to generate per month?"
        value={survey.reportsPerMonth}
        placeholder="e.g. 25"
        onChange={(v) => onSurveyChange({ reportsPerMonth: v })}
        error={errors.reportsPerMonth}
      />

      <div className="mb-5">
        <label className="mb-2 block text-sm font-semibold text-muted-foreground">
          How many pages do you typically review per report?
          <span className="ml-1 text-destructive">*</span>
        </label>
        <div className="-mb-5 grid grid-cols-2 gap-4 duration-200 animate-in fade-in zoom-in-95">
          <TextInput
            type="number"
            label="Min pages"
            value={survey.pagesMin}
            placeholder="Min"
            onChange={(v) => onSurveyChange({ pagesMin: v })}
          />
          <TextInput
            type="number"
            label="Max pages"
            value={survey.pagesMax}
            placeholder="Max"
            onChange={(v) => onSurveyChange({ pagesMax: v })}
          />
        </div>
        {errors.pages && <p className="mt-1 text-sm text-destructive-text">{errors.pages}</p>}
      </div>

      <div className="mb-6">
        <label className="mb-3 block text-sm font-semibold text-muted-foreground">
          Is your case flow consistent per month?
          <span className="ml-1 text-destructive">*</span>
        </label>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className={caseFlowLabelClass(survey.caseFlowConsistent === true)}>
            <input
              type="radio"
              name="caseFlowConsistent"
              checked={survey.caseFlowConsistent === true}
              onChange={() => onSurveyChange({ caseFlowConsistent: true })}
              className="sr-only"
            />
            <span className="text-sm font-medium">Yes, fairly consistent</span>
          </label>
          <label className={caseFlowLabelClass(survey.caseFlowConsistent === false)}>
            <input
              type="radio"
              name="caseFlowConsistent"
              checked={survey.caseFlowConsistent === false}
              onChange={() => onSurveyChange({ caseFlowConsistent: false })}
              className="sr-only"
            />
            <span className="text-sm font-medium">No, it varies</span>
          </label>
        </div>
        {errors.caseFlow && <p className="mt-2 text-sm text-destructive-text">{errors.caseFlow}</p>}
      </div>
    </div>
  );
};
